import React, { useState, useEffect } from 'react';
import { TextField, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Snackbar, Alert, InputAdornment } from "@mui/material";
import { Close, Visibility, VisibilityOff } from "@mui/icons-material";
import "./ChangePWwithEmailForm.css";
import { ChangePasswordEmail } from "../../../api/forgetpasswordApi/forgetpasswordApi";
import { useNavigate } from "react-router-dom";

const ChangePWwithEmailForm = ({ open, handleClose }) => {
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const passwordRequirements = [
    ['At least 8 characters', newPassword.length >= 8],
    ['At least 1 uppercase letter', /[A-Z]/.test(newPassword)],
    ['At least 1 lowercase letter', /[a-z]/.test(newPassword)],
    ['At least 1 number', /\d/.test(newPassword)],
    ['At least 1 special character', /[^A-Za-z0-9]/.test(newPassword)],
  ];

  // Get email from localStorage on component mount
  useEffect(() => {
    const storedEmail = localStorage.getItem('email');
    if (storedEmail) {
      setEmail(storedEmail); // Set the email to state
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      setError("Both password fields are required");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    const missingRequirements = passwordRequirements
      .filter(([, met]) => !met)
      .map(([label]) => label);
    if (missingRequirements.length) {
      setError(`Password requirements not met: ${missingRequirements.join(', ')}.`);
      return;
    }

    setLoading(true);

    try {
        // console.log("Email: ", email);
        // console.log("New Password: ", newPassword);
      await ChangePasswordEmail(email, newPassword);
      setAlertSeverity("success");
      setAlertMessage("Password changed successfully!");
      setOpenSnackbar(true);

      handleClose();

      setTimeout(() => {
        navigate("/profile");
      }, 500);
    } catch (error) {
      setAlertSeverity("error");
      setAlertMessage(error.response ? error.response.data.message : "Error resetting password. Try again.");
      setError("Error resetting password. Try again.");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} className="ChangePasswordDialog">
        <DialogTitle className="ChangePasswordHeader">
          Change Your Password
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "#6E0506",
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <form className="ChangePasswordForm-Form" onSubmit={handleSubmit}>
            <TextField
              type={showNewPassword ? 'text' : 'password'}
              placeholder="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="ChangePasswordInputField"
              fullWidth
              error={!!error && !newPassword}
              helperText={!newPassword ? error : ""}
              margin="normal"
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <ul className="password-requirements">
              {passwordRequirements.map(([label, met]) => (
                <li key={label} style={{ color: met ? "#16803c" : "#6E0506" }}>
                  {met ? "✓" : "✗"} {label}
                </li>
              ))}
            </ul>
            <TextField
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="ChangePasswordInputField"
              fullWidth
              error={!!error && newPassword !== confirmPassword}
              helperText={newPassword !== confirmPassword ? error : ""}
              margin="normal"
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <DialogActions>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  backgroundColor: "#6E0506 !important",
                  "&:hover": { backgroundColor: "#bf282a !important" },
                  color: "#fff",
                }}
                disabled={loading}
              >
                {loading ? "Updating..." : "Change Password"}
              </Button>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>

      <Snackbar open={openSnackbar} autoHideDuration={4000} onClose={() => setOpenSnackbar(false)}>
        <Alert onClose={() => setOpenSnackbar(false)} severity={alertSeverity} sx={{ width: "100%" }}>
          {alertMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ChangePWwithEmailForm;
